// ============================================================================
// JARVISTRAVEL - CUSTOM HOOKS
// Version: 1.0.0
// ============================================================================

import { useState, useEffect, useCallback, useRef, useMemo, useSyncExternalStore } from 'react';
import {
  getFromStorage,
  setToStorage,
  debounce
} from '@/app/utils';
import { ANIMATIONS } from '@/app/constants';
import { getBackendStatus, subscribeBackendStatus, type BackendStatus } from '@/app/api/backendStatus';

// -----------------------------------------------------------------------------
// useBackendStatus — live backend reachability (JAR-314)
// -----------------------------------------------------------------------------

/** Subscribe to backend reachability. Screens use this to pause actions that
 *  need the backend ('offline') and re-enable them on recovery ('online'). */
export function useBackendStatus(): BackendStatus {
  return useSyncExternalStore(subscribeBackendStatus, getBackendStatus, getBackendStatus);
}

// -----------------------------------------------------------------------------
// useLocalStorage
// -----------------------------------------------------------------------------

export function useLocalStorage<T>(
  key: string, 
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    return getFromStorage(key, initialValue);
  });
  
  const setValue = useCallback((value: T | ((val: T) => T)) => {
    setStoredValue(prev => {
      const valueToStore = value instanceof Function ? value(prev) : value;
      setToStorage(key, valueToStore);
      return valueToStore;
    });
  }, [key]);
  
  return [storedValue, setValue];
}

// -----------------------------------------------------------------------------
// useForm
// -----------------------------------------------------------------------------

/** Use with object types so they satisfy `Record<string, unknown>` (avoids `any`). */
export type FormValues<T extends object> = T & Record<string, unknown>;

interface FormState<T> {
  values: T;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
}

interface UseFormOptions<T> {
  initialValues: T;
  validate?: (values: T) => Partial<Record<keyof T, string>>;
  onSubmit: (values: T) => Promise<void> | void;
}

export function useForm<T extends Record<string, unknown>>({
  initialValues,
  validate,
  onSubmit,
}: UseFormOptions<T>) {
  const [state, setState] = useState<FormState<T>>({
    values: initialValues,
    touched: {},
    isSubmitting: false,
  });
  
  const errors = useMemo((): Partial<Record<keyof T, string>> => {
    if (!validate) return {};
    return validate(state.values);
  }, [validate, state.values]);
  
  const isValid = Object.keys(errors).length === 0;
  
  const handleChange = useCallback((field: keyof T, value: unknown) => {
    setState(prev => ({
      ...prev,
      values: { ...prev.values, [field]: value },
      touched: { ...prev.touched, [field]: true },
    }));
  }, []);
  
  const handleBlur = useCallback((field: keyof T) => {
    setState(prev => ({
      ...prev,
      touched: { ...prev.touched, [field]: true },
    }));
  }, []);
  
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    const allTouched = Object.keys(state.values).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {} as Partial<Record<keyof T, boolean>>
    );
    
    setState(prev => ({
      ...prev,
      touched: allTouched,
      isSubmitting: true,
    }));
    
    if (isValid) {
      try {
        await onSubmit(state.values);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    }
    
    setState(prev => ({ ...prev, isSubmitting: false }));
  }, [isValid, state.values, onSubmit]);
  
  const reset = useCallback(() => {
    setState({
      values: initialValues,
      touched: {},
      isSubmitting: false,
    });
  }, [initialValues]);
  
  const setFieldValue = useCallback((field: keyof T, value: unknown) => {
    handleChange(field, value);
  }, [handleChange]);
  
  const setFieldError = useCallback(() => {
    // Note: errors are now computed via useMemo, so setFieldError no longer modifies state
    // This function is kept for API compatibility but has no effect
    console.warn('setFieldError is deprecated when using computed validation');
  }, []);
  
  return {
    values: state.values,
    errors,
    touched: state.touched,
    isSubmitting: state.isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    reset,
    setFieldValue,
    setFieldError,
  };
}

// -----------------------------------------------------------------------------
// useDebounce
// -----------------------------------------------------------------------------

export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

// -----------------------------------------------------------------------------
// useSearch
// -----------------------------------------------------------------------------

interface UseSearchOptions<T> {
  items: T[];
  searchFields: (keyof T)[];
  debounceMs?: number;
}

export function useSearch<T>({ items, searchFields, debounceMs = 300 }: UseSearchOptions<T>) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, debounceMs);
  
  const results = useMemo(() => {
    if (!debouncedQuery.trim()) return items;
    
    const lowerQuery = debouncedQuery.toLowerCase();
    return items.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        return typeof value === 'string' && value.toLowerCase().includes(lowerQuery);
      })
    );
  }, [items, searchFields, debouncedQuery]);
  
  return { query, setQuery, results, isSearching: query !== debouncedQuery };
}

// -----------------------------------------------------------------------------
// useToggle
// -----------------------------------------------------------------------------

export function useToggle(initialValue: boolean = false): [boolean, () => void, (value: boolean) => void] {
  const [value, setValue] = useState(initialValue);
  
  const toggle = useCallback(() => setValue(v => !v), []);
  const set = useCallback((newValue: boolean) => setValue(newValue), []);
  
  return [value, toggle, set];
}

// -----------------------------------------------------------------------------
// useModal
// -----------------------------------------------------------------------------

interface UseModalReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useModal(initialState: boolean = false): UseModalReturn {
  const [isOpen, setIsOpen] = useState(initialState);
  
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(v => !v), []);
  
  return { isOpen, open, close, toggle };
}

// -----------------------------------------------------------------------------
// useClickOutside
// -----------------------------------------------------------------------------

export function useClickOutside<T extends HTMLElement>(
  callback: () => void
): React.RefObject<T | null> {
  const ref = useRef<T>(null);
  
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };
    
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [callback]);
  
  return ref;
}

// -----------------------------------------------------------------------------
// useCountdown
// -----------------------------------------------------------------------------

interface UseCountdownReturn {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

export function useCountdown(targetDate: string): UseCountdownReturn {
  const [timeLeft, setTimeLeft] = useState<UseCountdownReturn>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const target = new Date(targetDate).getTime();
      const now = new Date().getTime();
      const difference = target - now;
      
      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
      }
      
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        isExpired: false,
      };
    };
    
    setTimeLeft(calculateTimeLeft());
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
}

// -----------------------------------------------------------------------------
// useLoader
// -----------------------------------------------------------------------------

export function useLoader(duration: number = ANIMATIONS.loader) {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const increment = 100 / (duration / 50);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(interval);
          setTimeout(() => setIsLoading(false), 200);
          return 100;
        }
        return next;
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, [duration]);
  
  return { isLoading, progress };
}

// -----------------------------------------------------------------------------
// usePrevious
// -----------------------------------------------------------------------------

export function usePrevious<T>(value: T): T | undefined {
  // Derived during render, not from a ref (JAR-2203: reading ref.current here is a
  // render-phase read) and not from an effect that mirrors the value into state -
  // that returns the NEW value on the following render, which is not "previous".
  // React's documented "adjust state when a prop changes" pattern keeps both and
  // returns the older one.
  const [seen, setSeen] = useState<{ current: T; previous: T | undefined }>({
    current: value,
    previous: undefined,
  });
  if (seen.current !== value) {
    setSeen({ current: value, previous: seen.current });
  }
  return seen.previous;
}

// -----------------------------------------------------------------------------
// useWindowSize
// -----------------------------------------------------------------------------

interface WindowSize {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export function useWindowSize(): WindowSize {
  const [size, setSize] = useState<WindowSize>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    isMobile: false,
    isTablet: false,
    isDesktop: true,
  });
  
  useEffect(() => {
    const handleResize = debounce(() => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setSize({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
      });
    }, 100);
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return size;
}

// -----------------------------------------------------------------------------
// useKeyPress
// -----------------------------------------------------------------------------

export function useKeyPress(targetKey: string, callback: () => void): void {
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === targetKey) {
        callback();
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [targetKey, callback]);
}

// -----------------------------------------------------------------------------
// useAsync
// -----------------------------------------------------------------------------

interface AsyncState<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
}

export function useAsync<T>(
  asyncFunction: () => Promise<T>,
  dependencies: unknown[] = []
): AsyncState<T> & { execute: () => Promise<void> } {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    error: null,
    isLoading: false,
  });
  
  const execute = useCallback(async () => {
    setState({ data: null, error: null, isLoading: true });
    
    try {
      const data = await asyncFunction();
      setState({ data, error: null, isLoading: false });
    } catch (error) {
      setState({ data: null, error: error as Error, isLoading: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-supplied deps are spread in by design
  }, [asyncFunction, ...dependencies]);
  
  return { ...state, execute };
}

// -----------------------------------------------------------------------------
// useScrollPosition
// -----------------------------------------------------------------------------

export function useScrollPosition(): { x: number; y: number } {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleScroll = () => {
      setPosition({
        x: window.scrollX,
        y: window.scrollY,
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return position;
}

// -----------------------------------------------------------------------------
// useInterval
// -----------------------------------------------------------------------------

export function useInterval(callback: () => void, delay: number | null): void {
  const savedCallback = useRef(callback);
  
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  
  useEffect(() => {
    if (delay === null) return;
    
    const tick = () => savedCallback.current();
    const id = setInterval(tick, delay);
    
    return () => clearInterval(id);
  }, [delay]);
}
